import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
    Search,
    Plus,
    Save,
    Trash2,
    FileCheck,
    Loader2,
    User,
    Calendar
} from 'lucide-react';

// Tipler
interface Student {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
}

interface ExamRecord {
    id?: string;
    student_id: string;
    cisia_username: string;
    cisia_password: string;
    cisia_exam_date: string;
    tolc_score: string;
    tolc_fee: number;
    application_date: string;
    college_board_username: string;
    college_board_password: string;
    is_new?: boolean;
}

const ExamsPage: React.FC = () => {
    const { user } = useAuth();
    const [students, setStudents] = useState<Student[]>([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [exams, setExams] = useState<ExamRecord[]>([]);
    const [loadingExams, setLoadingExams] = useState(false);

    // Öğrencileri getir
    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        setLoadingStudents(true);
        try {
            // Operations öğrencilerini getir
            let query = supabase
                .from('operation_students')
                .select('id, first_name, last_name, email')
                .order('first_name');

            if (user?.branchId) query = query.eq('branch_id', user.branchId);
            const { data, error } = await query;

            if (error) throw error;
            setStudents(data || []);
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoadingStudents(false);
        }
    };

    // Öğrenci seçilince sınavları getir
    useEffect(() => {
        if (selectedStudent) {
            fetchExams(selectedStudent.id);
        } else {
            setExams([]);
        }
    }, [selectedStudent]);

    const fetchExams = async (studentId: string) => {
        setLoadingExams(true);
        try {
            const { data, error } = await supabase
                .from('operation_exam_records')
                .select('*')
                .eq('student_id', studentId)
                .order('created_at', { ascending: true });

            if (error) throw error;
            setExams(data || []);
        } catch (error) {
            console.error('Error fetching exams:', error);
        } finally {
            setLoadingExams(false);
        }
    };

    const handleAddRow = () => {
        if (!selectedStudent) return;
        const newRecord: ExamRecord = {
            student_id: selectedStudent.id,
            cisia_username: '',
            cisia_password: '',
            cisia_exam_date: '',
            tolc_score: '',
            tolc_fee: 0,
            application_date: new Date().toISOString().split('T')[0],
            college_board_username: '',
            college_board_password: '',
            is_new: true
        };
        setExams([...exams, newRecord]);
    };

    const handleChange = (index: number, field: keyof ExamRecord, value: any) => {
        const newExams = [...exams];
        newExams[index] = { ...newExams[index], [field]: value };
        setExams(newExams);
    };

    const handleSaveRow = async (index: number) => {
        const record = exams[index];
        try {
            const payload = {
                student_id: record.student_id,
                cisia_username: record.cisia_username,
                cisia_password: record.cisia_password,
                cisia_exam_date: record.cisia_exam_date || null,
                tolc_score: record.tolc_score,
                tolc_fee: record.tolc_fee || 0,
                application_date: record.application_date || null,
                college_board_username: record.college_board_username,
                college_board_password: record.college_board_password
            };

            let result;
            if (record.id && !record.is_new) {
                // Update
                const { data, error } = await supabase
                    .from('operation_exam_records')
                    .update(payload)
                    .eq('id', record.id)
                    .select()
                    .single();
                if (error) throw error;
                result = data;
            } else {
                // Insert
                const { data, error } = await supabase
                    .from('operation_exam_records')
                    .insert(payload)
                    .select()
                    .single();
                if (error) throw error;
                result = data;
            }

            // Update local state
            const newExams = [...exams];
            newExams[index] = { ...result, is_new: false };
            setExams(newExams);
            // alert('Kayıt başarıyla güncellendi.');
        } catch (error) {
            console.error('Error saving record:', error);
            alert('Kaydetme hatası oluştu.');
        }
    };

    const handleDeleteRow = async (index: number) => {
        const record = exams[index];
        if (!record.id) {
            // Henüz kaydedilmemiş satır
            const newExams = exams.filter((_, i) => i !== index);
            setExams(newExams);
            return;
        }

        if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('operation_exam_records')
                .delete()
                .eq('id', record.id);

            if (error) throw error;

            const newExams = exams.filter((_, i) => i !== index);
            setExams(newExams);
        } catch (error) {
            console.error('Delete error:', error);
            alert('Silme sırasında hata oluştu.');
        }
    };

    const filteredStudents = students.filter(s =>
        `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="h-[calc(100vh-2rem)] flex gap-6 p-6">
            {/* Sol Panel - Öğrenci Listesi */}
            <div className="w-1/4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Öğrenciler</h2>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Öğrenci ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {loadingStudents ? (
                        <div className="flex justify-center p-4"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
                    ) : (
                        filteredStudents.map(student => (
                            <button
                                key={student.id}
                                onClick={() => setSelectedStudent(student)}
                                className={`w-full text-left p-3 rounded-xl transition-all flex items-center gap-3 ${selectedStudent?.id === student.id
                                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300 border border-transparent'
                                    }`}
                            >
                                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold shrink-0">
                                    {student.first_name?.[0]}{student.last_name?.[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold truncate">{student.first_name} {student.last_name}</p>
                                    <p className="text-xs text-gray-500 truncate">{student.email}</p>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>

            {/* Sağ Panel - Sınav Tablosu */}
            <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
                {selectedStudent ? (
                    <>
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <FileCheck className="w-6 h-6 text-blue-600" />
                                    Sınav Kayıtları
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedStudent.first_name} {selectedStudent.last_name} için sınav bilgileri
                                </p>
                            </div>
                            <button
                                onClick={handleAddRow}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Yeni Kayıt
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-6">
                            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 font-medium">
                                        <tr>
                                            <th className="p-3 text-left min-w-[150px]">CISIA Kullanıcı Adı</th>
                                            <th className="p-3 text-left min-w-[120px]">CISIA Şifre</th>
                                            <th className="p-3 text-left min-w-[130px]">CISIA Sınav Tarihi</th>
                                            <th className="p-3 text-left min-w-[100px]">TOLC Sonuç</th>
                                            <th className="p-3 text-left min-w-[100px]">TOLC Ücreti</th>
                                            <th className="p-3 text-left min-w-[130px]">Başvuru Tarihi</th>
                                            <th className="p-3 text-left min-w-[150px]">College Board KA</th>
                                            <th className="p-3 text-left min-w-[120px]">College Board Şifre</th>
                                            <th className="p-3 text-center w-24 sticky right-0 bg-gray-50 dark:bg-gray-900/50">İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                        {exams.map((exam, idx) => (
                                            <tr key={exam.id || idx} className="group hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                <td className="p-2">
                                                    <input
                                                        type="text"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white placeholder-gray-300"
                                                        placeholder="Kullanıcı Adı"
                                                        value={exam.cisia_username || ''}
                                                        onChange={e => handleChange(idx, 'cisia_username', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="text"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white placeholder-gray-300"
                                                        placeholder="Şifre"
                                                        value={exam.cisia_password || ''}
                                                        onChange={e => handleChange(idx, 'cisia_password', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="date"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white"
                                                        value={exam.cisia_exam_date || ''}
                                                        onChange={e => handleChange(idx, 'cisia_exam_date', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="text"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white placeholder-gray-300"
                                                        placeholder="Sonuç"
                                                        value={exam.tolc_score || ''}
                                                        onChange={e => handleChange(idx, 'tolc_score', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="number"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white placeholder-gray-300"
                                                        placeholder="0.00"
                                                        value={exam.tolc_fee || ''}
                                                        onChange={e => handleChange(idx, 'tolc_fee', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="date"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white"
                                                        value={exam.application_date || ''}
                                                        onChange={e => handleChange(idx, 'application_date', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="text"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white placeholder-gray-300"
                                                        placeholder="Kullanıcı Adı"
                                                        value={exam.college_board_username || ''}
                                                        onChange={e => handleChange(idx, 'college_board_username', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="text"
                                                        className="w-full bg-transparent border-b border-transparent focus:border-blue-500 focus:outline-none p-1 text-gray-900 dark:text-white placeholder-gray-300"
                                                        placeholder="Şifre"
                                                        value={exam.college_board_password || ''}
                                                        onChange={e => handleChange(idx, 'college_board_password', e.target.value)}
                                                    />
                                                </td>
                                                <td className="p-2 text-center sticky right-0 bg-white dark:bg-gray-800 shadow-sm">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button
                                                            onClick={() => handleSaveRow(idx)}
                                                            className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                                            title="Kaydet"
                                                        >
                                                            <Save className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteRow(idx)}
                                                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                            title="Sil"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {exams.length === 0 && (
                                            <tr>
                                                <td colSpan={9} className="p-8 text-center text-gray-500 italic">
                                                    Henüz sınav kaydı bulunmamaktadır. "Yeni Kayıt" butonu ile ekleyebilirsiniz.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                        <User className="w-16 h-16 mb-4 opacity-20" />
                        <p>İşlem yapmak için soldan bir öğrenci seçiniz.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamsPage;
